// Types for Archie Showcase Section

export type CapabilityCardType =
  | 'excel-analysis'
  | 'excel-sidebar'
  | 'microsoft-integration'
  | 'teams-meetings'
  | 'bulk-registration'
  | 'state-tax-filing'
  | 'tax-guidance'
  | 'bookkeeping-guidance'
  | 'technical-accounting'
  | 'variance-analysis';

export type ShowcaseSlide = {
  id: string;
  title: string;
  description: string;
  cardType: CapabilityCardType;
};

// Category type for grouping slides
export type ShowcaseCategory = {
  id: string;
  label: string;
  slideRange: [number, number]; // [startIndex, endIndex] inclusive
};

export const SHOWCASE_CATEGORIES: ShowcaseCategory[] = [
  { id: 'meets-you', label: 'Meets You Where You Are', slideRange: [0, 3] },
  { id: 'scale', label: 'Scale Without Hiring', slideRange: [4, 5] },
  { id: 'expert-help', label: 'Expert Help On Demand', slideRange: [6, 9] },
];

export const SHOWCASE_SLIDES: ShowcaseSlide[] = [
  {
    id: 'excel-analysis',
    cardType: 'excel-analysis',
    title: 'Excel Inside Archie',
    description:
      'Archie automatically understands spreadsheet structure, detecting table types, identifying column meanings (dates, amounts, account codes), and recognizing complex patterns like multi-row headers and hierarchies.',
  },
  {
    id: 'excel-sidebar',
    cardType: 'excel-sidebar',
    title: 'Excel Sidebar',
    description:
      "In your spreadsheet select a range, and using the sidebar ask Archie in plain English what you'd like. Get instant accounting intelligence variance drivers with materiality flags, balance validation, anomaly detection, and formatted outputs.",
  },
  {
    id: 'microsoft-integration',
    cardType: 'microsoft-integration',
    title: 'Work Where You Already Work',
    description:
      'Archie appears in Excel, Outlook, Teams, Copilot, and across your Microsoft environment. During meetings, Archie transcribes conversations, tracks action items, and provides real-time accounting guidance. In spreadsheets, he can understand your data context and offers intelligent suggestions without switching applications.',
  },
  {
    id: 'teams-meetings',
    cardType: 'teams-meetings',
    title: 'Turn Teams Meetings Into Structured Actions',
    description:
      'Add Archie to your client review or internal planning meeting in Teams. Ask questions live during the call and Archie answers using real data and documents. After the meeting, you get a summary with decisions, agreed actions, and deep links back to Archie, no more scattered notes or lost follow-ups.',
  },
  {
    id: 'bulk-registration',
    cardType: 'bulk-registration',
    title: 'Register 4,000 Accounts Before the Deadline While You Sleep',
    description:
      'Archie logs into HMRC portals, reads data from your spreadsheet, completes multi-page forms for each registration, and processes the entire list autonomously.',
  },
  {
    id: 'state-tax-filing',
    cardType: 'state-tax-filing',
    title: 'File in 30 States in 2 Hours Instead of 2 Weeks',
    description:
      "Archie navigates each state portal using browser automation, extracts sales data from your GL, completes forms, handles calculations and conversions, and maintains filing records for audit trails. Whether it's 30 portals or 30,000 submissions.",
  },
  {
    id: 'tax-guidance',
    cardType: 'tax-guidance',
    title: 'Get Tax Guidance Without Consulting Specialists',
    description:
      'Your junior has a Section 179 question at 6pm. Instead of waiting until tomorrow to ask the tax partner, they ask Archie and get jurisdiction-specific guidance with accurate IRC citations. Work keeps moving.',
  },
  {
    id: 'bookkeeping-guidance',
    cardType: 'bookkeeping-guidance',
    title: 'Turn Complex Bookkeeping Questions Into Clear Steps',
    description:
      'Archie provides transaction recording guidance, bank reconciliation procedures, and software-specific help for QuickBooks or Xero. Proper chart of accounts usage and double-entry guidance on demand.',
  },
  {
    id: 'variance-analysis',
    cardType: 'variance-analysis',
    title: 'Access FP&A Expertise for\nVariance Analysis',
    description:
      'Archie creates financial models, calculates and interprets ratios, and performs variance analysis. Get standards-compliant analysis with actionable insights from your data.',
  },
  {
    id: 'technical-accounting',
    cardType: 'technical-accounting',
    title: 'Get Technical Accounting Answers 24/7',
    description:
      'Ask Archie about ASC 842 lease classification, ASC 606 revenue recognition, or any technical standard, and get accurate, sourced guidance with explanations in context. Like having a technical accounting specialist available around the clock.',
  },
];
